import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import { TradeItem } from '@/types';
import type { ColumnConfig } from '@/stores/trades/mtm-column-store';
import { MTM_SYNTHETIC_NEW_PRICE, MTM_SYNTHETIC_PRICE_IDS } from '@/stores/trades/mtm-column-store';

const SYNTHETIC_ID_SET = new Set<string>(MTM_SYNTHETIC_PRICE_IDS);

/**
 * Build the ordered, visibility-filtered list of MTM column defs from the column
 * store config. Real ids resolve to their createMtmColumns def; synthetic
 * price-block ids resolve to a lightweight placeholder def (id + header label) —
 * MtmTable renders those cells itself from per-row helpers.
 *
 * Shared by MtmPage and the MTM table tests so both build columns identically.
 */
export function buildMtmColumnDefs(
  columnConfig: ColumnConfig[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allColumns: ColumnDef<TradeItem, any>[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<TradeItem, any>[] {
  const byId = new Map(allColumns.map((c) => [getColumnId(c), c]));
  return columnConfig
    .filter((cfg) => cfg.visible)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((cfg): ColumnDef<TradeItem, any> | undefined => {
      if (SYNTHETIC_ID_SET.has(cfg.id)) {
        // `meta.align` mirrors the alignment MtmTable hardcodes for these ids: every
        // synthetic cell is a right-aligned figure except the New Price input. The
        // header switch sets its own alignment, but the FOOTER cell reads it from
        // here (columnAlign), so without it a synthetic column total (LR-538) would
        // sit left-aligned under right-aligned data.
        const align = cfg.id === MTM_SYNTHETIC_NEW_PRICE ? 'left' : 'right';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return { id: cfg.id, header: cfg.label, meta: { align } } as ColumnDef<TradeItem, any>;
      }
      return byId.get(cfg.id);
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((c): c is ColumnDef<TradeItem, any> => c != null);
}
